import React, { useState, useEffect } from "react";
import helpDocs from "./doc.json";
import { fetchUserData } from "../../redux/userActions.js";
import { useSelector, useDispatch } from "react-redux";

// Lazy image import
const imageImports = import.meta.glob(
  "../../assets/HelpDocsGifs/**/*.{gif,png,jpg,jpeg}"
);

function LazyImage({ imagePath, alt }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      try {
        const key = Object.keys(imageImports).find((k) =>
          k.endsWith(imagePath)
        );
        if (key) {
          const module = await imageImports[key]();
          setSrc(module.default);
        }
      } catch (err) {
        console.error("Image load error:", err);
      }
    };
    loadImage();
  }, [imagePath]);

  if (!src) {
    return (
      <div className="w-full h-40 bg-gray-100 flex items-center justify-center text-sm text-gray-500">
        Loading image...
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="rounded-lg shadow-md border mt-3"
    />
  );
}

export default function HelpCenter() {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);

  const roleMap = {
    0: "SUPER ADMIN",
    1: "ADMIN",
    2: "PARTNER",
    3: "TEAM LEAD",
  };
  const [selectedRole, setSelectedRole] = useState("");
  const [activeSlug, setActiveSlug] = useState("introduction");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!userDetails && token) {
      dispatch(fetchUserData());
    }
  }, [dispatch, userDetails]);

  useEffect(() => {
    if (userDetails?.role !== undefined) {
      const mapped = roleMap[userDetails.role];
      if (mapped) {
        setSelectedRole(mapped);
      }
    }
  }, [userDetails]);

  const allPages = [
    { group: "Getting Started", pages: helpDocs.common || [] },
    ...(selectedRole && helpDocs.roles[selectedRole]
      ? helpDocs.roles[selectedRole]
      : []),
  ];

  // {
  //   allPages.map((group, i) => (
  //     <div key={i} className="mb-6">
  //       <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
  //         {group.group}
  //       </h3>

  //       {(group.pages || []).map((page) => (
  //         <div
  //           key={page.slug}
  //           onClick={() => setActiveSlug(page.slug)}
  //           className={`cursor-pointer px-3 py-2 rounded-md text-sm mb-1 transition 
  //       ${page.slug === activeSlug
  //               ? "bg-blue-100 text-blue-600 font-medium"
  //               : "hover:bg-gray-100"
  //             }`}
  //         >
  //           {page.title}
  //         </div>
  //       ))}
  //     </div>
  //   ))
  // }

  const sidebarPages = allPages.flatMap((g) => g.pages || []);
  const currentIndex = sidebarPages.findIndex(
    (p) => p.slug === activeSlug
  );
  const activeDoc = sidebarPages.find(
    (p) => p.slug === activeSlug
  ) || {};

  return (
    <div className="flex h-screen bg-gray-50">
      {/* SIDEBAR */}
      <aside className="w-72 bg-white border-r overflow-y-auto p-4">
        {allPages.map((group, i) => (
          <div key={i} className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
              {group.group}
            </h3>

            {(group.pages || []).map((page) => (
              <div
                key={page.slug}
                onClick={() => setActiveSlug(page.slug)}
                className={`cursor-pointer px-3 py-2 rounded-md text-sm mb-1 transition 
                ${page.slug === activeSlug
                    ? "bg-blue-100 text-blue-600 font-medium"
                    : "hover:bg-gray-100"
                  }`}
              >
                {page.title}
              </div>
            ))}
          </div>
        ))}
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-y-auto p-8">
        <h1 className="text-2xl font-bold mb-4">
          {activeDoc?.title}
        </h1>

        {/* CONTENT */}
        {Array.isArray(activeDoc?.content) ? (
          activeDoc.content.map((p, i) => (
            <p key={i} className="text-gray-700 mb-2">
              {p}
            </p>
          ))
        ) : (
          <p className="text-gray-700 mb-4">
            {activeDoc?.content}
          </p>
        )}

        {/* SECTIONS */}
        {activeDoc?.sections?.map((section, i) => (
          <div
            key={i}
            className="bg-white border rounded-xl p-5 shadow-sm mb-6"
          >
            <h2 className="text-lg font-semibold mb-2">
              {section.heading}
            </h2>

            <p className="text-gray-600 mb-3">
              {section.intro}
            </p>

            {section.imageUrl && (
              <LazyImage imagePath={section.imageUrl} />
            )}

            {section.details && (
              <ol className="list-decimal pl-5 mt-3 space-y-1 text-sm text-gray-700">
                {section.details.map((d, idx) => (
                  <li key={idx}>{d}</li>
                ))}
              </ol>
            )}
          </div>
        ))}

        {/* NAV BUTTONS */}
        <div className="flex justify-between mt-6">
          {currentIndex > 0 && (
            <button
              onClick={() =>
                setActiveSlug(
                  sidebarPages[currentIndex - 1].slug
                )
              }
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Back
            </button>
          )}

          {currentIndex < sidebarPages.length - 1 && (
            <button
              onClick={() =>
                setActiveSlug(
                  sidebarPages[currentIndex + 1].slug
                )
              }
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Next
            </button>
          )}
        </div>
      </main>
    </div>
  );
}